import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/services/supabase/supabase';
import { getTemplateCategories, getTemplates } from '@/services/supabase/templates';
import { ArrowLeft, PlusCircle, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';


interface Template {
  id: number;
  name: string;
  description: string | null;
  category_id: number;
  category_name?: string;
}

interface Category {
  id: number;
  name: string;
  description: string | null;
}

function EntityCreatePage() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  // const { hasAIFeatures } = useLicense(); // Removed unused


  // Extract parameters from query parameters if present
  const searchParams = new URLSearchParams(location.search);
  const storyId = searchParams.get('storyId');
  const defaultCategoryId = searchParams.get('categoryId');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('active');
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New category dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);



  useEffect(() => {
    if (!projectId) {
      setError("Missing project ID");
      setIsLoading(false);
      return;
    }

    fetchTemplatesAndCategories();
  }, [projectId]);

  // Set default category ID if provided in URL
  useEffect(() => {
    if (defaultCategoryId && categories.length > 0) {
      const categoryId = parseInt(defaultCategoryId);
      if (categories.some(cat => cat.id === categoryId)) {
        setSelectedCategoryId(categoryId);
      }
    }
  }, [defaultCategoryId, categories]);

  useEffect(() => {
    if (selectedCategoryId === null) {
      setFilteredTemplates(templates);
    } else {
      setFilteredTemplates(templates.filter(template => template.category_id === selectedCategoryId));
      // If current template is not in the filtered list, reset it
      if (selectedTemplateId && !templates.find(t => t.id === selectedTemplateId && t.category_id === selectedCategoryId)) {
        setSelectedTemplateId(null);
      }
    }
  }, [selectedCategoryId, templates, selectedTemplateId]);

  const fetchTemplatesAndCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch categories for this project
      const categoriesData = await getTemplateCategories(Number(projectId));
      const visibleCategories = categoriesData.filter(category => category.is_visible_in_dropdown);
      setCategories(visibleCategories || []);

      // Fetch templates for this project
      const templatesData = await getTemplates(Number(projectId));
      const publicTemplates = templatesData.filter(template => template.is_public);

      const formattedTemplates = publicTemplates.map(template => ({
        id: template.id,
        name: template.name,
        description: template.description,
        category_id: template.category_id,
        category_name: template.category_name || 'Uncategorized'
      }));

      setTemplates(formattedTemplates);
      setFilteredTemplates(formattedTemplates);

    } catch (err) {
      console.error('Error fetching templates and categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(Number(categoryId) || null);
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(Number(templateId));

    // Pre-fill name based on template if name is empty
    const selectedTemplate = templates.find(t => t.id === Number(templateId));
    if (selectedTemplate && !name) {
      setName(`New ${selectedTemplate.name}`);
    }
  };

  // New function to create a category
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      setIsSubmitting(true);

      // Create the category
      const { data: newCategory, error: categoryError } = await supabase
        .from('template_categories')
        .insert([
          {
            name: newCategoryName,
            description: newCategoryDescription,
            is_visible_in_dropdown: true
          }
        ])
        .select()
        .single();

      if (categoryError) throw categoryError;

      // Create a default template for this category
      const { error: templateError } = await supabase
        .from('templates')
        .insert([
          {
            category_id: newCategory.id,
            name: `${newCategoryName}`,
            description: `Default template for ${newCategoryName.toLowerCase()}`,
            is_public: true
          }
        ]);

      if (templateError) throw templateError;

      toast({
        title: "Success",
        description: `${newCategoryName} group created successfully`,
      });

      // Refresh groups and templates
      await fetchTemplatesAndCategories();

      // Auto-select the new group
      setSelectedCategoryId(newCategory.id);

      // Close dialog and reset form
      setIsDialogOpen(false);
      setNewCategoryName('');
      setNewCategoryDescription('');

    } catch (err) {
      console.error('Error creating category:', err);
      toast({
        title: "Error",
        description: "Failed to create the new group",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !selectedTemplateId || !selectedCategoryId) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields (Name, Group, and Type)",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSaving(true);

      // Create the entity
      const { data: entityData, error: entityError } = await supabase
        .from('entities')
        .insert([
          {
            name,
            description,
            status,
            template_id: selectedTemplateId,
            category_id: selectedCategoryId,
            project_id: projectId
          }
        ])
        .select()
        .single();

      if (entityError) throw entityError;

      if (!entityData) {
        throw new Error('Failed to create item');
      }

      // If creating from story view, add to story
      if (storyId) {
        const { error: storyError } = await supabase
          .from('story_entities')
          .insert([
            {
              story_id: storyId,
              entity_id: entityData.id,
              role: 'supporting' // Default role
            }
          ]);

        if (storyError) throw storyError;
      }

      toast({
        title: "Success",
        description: "Item created successfully",
        variant: "default"
      });

      // Navigate to entity details
      // If we have a story ID, we should navigate to the overview page
      // since the story page doesn't exist
      if (storyId) {
        navigate(`/projects/${projectId}/overview`);
      } else {
        navigate(`/projects/${projectId}/entity/${entityData.id}`);
      }

    } catch (err) {
      console.error('Error creating entity:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : 'Failed to create item',
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-2 text-destructive">
          <p className="text-sm">{error}</p>
          <Button
            onClick={() => navigate(`/projects/${projectId}`)}
            variant="outline"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Return to Project
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6">
      <div className="mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(storyId ? `/projects/${projectId}/overview` : `/projects/${projectId}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {storyId ? 'Back to Overview' : 'Back to Project'}
        </Button>
      </div>

      <Card className="max-w-3xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Create New Item</CardTitle>
              <CardDescription>
                Add a new item to your project
              </CardDescription>
            </div>

          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="category" className="required">Group</Label>
              <div className="flex gap-2 items-center">
                <Select
                  value={selectedCategoryId?.toString() || ""}
                  onValueChange={handleCategoryChange}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsDialogOpen(true)}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </div>
              {categories.length === 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  No groups available. Please create a new group first.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="template" className="required">Type</Label>
              <Select
                value={selectedTemplateId?.toString() || ""}
                onValueChange={handleTemplateChange}
                disabled={!selectedCategoryId}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  {filteredTemplates.map(template => (
                    <SelectItem key={template.id} value={template.id.toString()}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCategoryId && filteredTemplates.length === 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  No types available for this group
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="name" className="required">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter a description"
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={status}
                onValueChange={(value) => setStatus(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </form>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || !name.trim() || !selectedTemplateId || !selectedCategoryId}
          >
            {isSaving ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-b-2 border-background"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Create Item
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      {/* Category Creation Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Group</DialogTitle>
            <DialogDescription>
              Add a new group to organize your content (e.g., Characters, Locations, Items)
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dialogName" className="text-right">
                Name
              </Label>
              <Input
                id="dialogName"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="col-span-3"
                placeholder="e.g., Vehicles, Artifacts, Factions"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dialogDescription" className="text-right">
                Description
              </Label>
              <Textarea
                id="dialogDescription"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                className="col-span-3"
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim() || isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}

export default EntityCreatePage;